import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { 
  GitBranch, 
  Zap, 
  FileText,
  Monitor,
  RefreshCw
} from 'lucide-react';
import { Card, CardContent } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Navigate } from 'react-router-dom';

// Import existing page components to use as tab content
import WorkflowBuilder from './WorkflowBuilder';
import AutomationRules from './AutomationRules';
import WorkflowTemplates from './WorkflowTemplates';
import WorkflowMonitoring from './WorkflowMonitoring';

const UnifiedWorkflow = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const initialTab = searchParams.get('tab') || 'builder';
  const [activeTab, setActiveTab] = useState(initialTab);

  // Update URL when tab changes
  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setSearchParams({ tab: tabId });
  };

  // Sync activeTab with URL params
  useEffect(() => {
    const tabFromUrl = searchParams.get('tab') || 'builder';
    if (tabFromUrl !== activeTab) {
      setActiveTab(tabFromUrl);
    }
  }, [searchParams]);

  const tabs = [
    { 
      id: 'builder', 
      label: 'Workflow Builder', 
      icon: GitBranch,
      description: 'Build and design workflows'
    },
    { 
      id: 'automation', 
      label: 'Automation Rules', 
      icon: Zap,
      description: 'Manage automation rules'
    },
    { 
      id: 'templates', 
      label: 'Templates', 
      icon: FileText,
      description: 'Workflow templates library'
    },
    { 
      id: 'monitoring', 
      label: 'Monitoring', 
      icon: Monitor,
      description: 'Monitor workflow execution'
    }
  ];

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
              <GitBranch className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Workflow Management</h1>
              <p className="text-gray-600 dark:text-gray-400 mt-1">
                Build, automate, and monitor workflows
              </p>
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={() => navigate('/admin/dashboard')}
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Back to Dashboard
          </Button>
        </div>
      </div>

      {/* Tab Navigation */}
      <Card>
        <CardContent className="p-0">
          <div className="border-b border-gray-200 dark:border-gray-700">
            <nav className="flex space-x-1 overflow-x-auto px-4" aria-label="Workflow Tabs">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => handleTabChange(tab.id)}
                    className={`flex items-center space-x-2 py-4 px-4 border-b-2 font-medium text-sm transition-colors whitespace-nowrap ${
                      activeTab === tab.id
                        ? 'border-[#006600] text-[#006600]'
                        : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 hover:border-gray-300'
                    }`}
                    title={tab.description}
                  >
                    <Icon className="h-4 w-4" />
                    <span>{tab.label}</span>
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {activeTab === 'builder' && (
              <div>
                <WorkflowBuilder />
              </div>
            )}

            {activeTab === 'automation' && (
              <div>
                <AutomationRules />
              </div>
            )}

            {activeTab === 'templates' && (
              <div>
                <WorkflowTemplates />
              </div>
            )}

            {activeTab === 'monitoring' && (
              <div>
                <WorkflowMonitoring />
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default UnifiedWorkflow;

